import { motion } from "framer-motion";
import HeroSection from "@/components/HeroSection";
import DestinationCard from "@/components/DestinationCard";
import ServiceCard from "@/components/ServiceCard";
import USPSection from "@/components/USPSection";
import ImageGrid from "@/components/ImageGrid";
import Partners from "@/components/Partners";
import WhatsAppBubble from "@/components/WhatsAppBubble";
import TourShowcase from "@/components/TourShowcase";
import ClientReviews from "@/components/ClientReviews";
import { Button } from "@/components/ui/button";
import { useLocation } from "wouter";
import { Car, Plane, Hotel, Compass, Map, Bus, Binoculars, ArrowRight } from "lucide-react";
import OptimizedImage from "@/components/OptimizedImage";
import { useLanguage } from "@/lib/LanguageContext";
import { useEffect } from "react";

import WhatsAppLeadCapture from "@/components/WhatsAppLeadCapture";

const featuredItineraries = [
  {
    title: "5-Day Uganda Gorilla Trekking",
    description: "Trek through the misty forests of Bwindi Impenetrable National Park to encounter endangered mountain gorillas in their natural habitat.",
    image: "/gorilla.webp",
    duration: "5 Days",
    price: "From $2,500",
    highlights: [
      "Mountain gorilla trekking",
      "Cultural village visits",
      "Nature walks",
      "Bird watching"
    ],
    href: "/tours/uganda/bwindi-gorilla-5-day"
  },
  {
    title: "Uganda Wildlife Safari",
    description: "A comprehensive wildlife experience across Uganda's most spectacular national parks and reserves.",
    image: "/5O5A9186.webp",
    duration: "5 Days",
    price: "From $1,800",
    highlights: [
      "Multiple game drives",
      "Boat safaris",
      "Nature walks",
      "Cultural experiences"
    ],
    href: "/tours/uganda/wildlife-5-day"
  },
  {
    title: "East Africa Multi-Country Tour",
    description: "The ultimate primate and wildlife adventure combining gorillas, chimpanzees, and classic safari experiences.",
    image: "/chimpanzee.jpg", // Note: Using existing image, ideally should check if a better one exists but this is safe
    duration: "8 Days",
    price: "From $3,200",
    highlights: [
      "Gorilla trekking",
      "Chimpanzee tracking",
      "Game drives",
      "Cultural immersion"
    ],
    href: "/tours/uganda/primates-wildlife-8-day"
  },
  {
    title: "3-Day Murchison Falls Adventure Safari",
    description: "Experience the world's most powerful waterfall and abundant wildlife in Uganda's largest and oldest conservation area.",
    image: "/murchison-falls.jpg",
    duration: "3 Days",
    price: "From $1,150",
    highlights: [
      "Game drives",
      "Boat cruise to the falls",
      "Top of the falls hike",
      "Rhino tracking in Ziwa"
    ],
    href: "/tours/uganda/murchison-falls-3-day"
  }
];

const services = [
  {
    title: "Car Hire",
    description: "4x4 vehicles perfect for safari adventures",
    icon: Car,
    href: "/services/car-hire",
    features: [
      "Latest model 4x4 vehicles",
      "Professional drivers available",
      "Comprehensive insurance coverage",
      "Custom itinerary planning"
    ]
  },
  {
    title: "Air Ticketing",
    description: "Domestic and international flight bookings",
    icon: Plane,
    href: "/services/air-ticketing",
    features: [
      "Best deals on flights",
      "Multiple airline options",
      "Group booking discounts",
      "24/7 travel support"
    ]
  },
  {
    title: "Accommodation",
    description: "Luxury lodges to tented camps",
    icon: Hotel,
    href: "/services/accommodation",
    features: [
      "Luxury safari lodges",
      "Tented wilderness camps",
      "Boutique hotels",
      "All-inclusive packages"
    ]
  },
  {
    title: "Wildlife & Birding Safaris",
    description: "Guided wildlife viewing experiences",
    icon: Binoculars,
    href: "/services/wildlife-birding-safaris",
    features: [
      "Expert wildlife guides",
      "Custom safari packages",
      "Photography tours",
      "Conservation focused"
    ]
  },
  {
    title: "Soft & Extreme Adventures",
    description: "Thrilling Adventures Across East Africa",
    icon: Compass,
    href: "/services/soft-extreme-adventures",
    features: [
      "White water rafting",
      "Mountain climbing",
      "Gorilla trekking",
      "Forest hiking"
    ]
  },
  {
    title: "Airport Transportation",
    description: "Reliable Airport Transport Services",
    icon: Bus,
    href: "/services/airport-transport",
    features: [
      "Meet & greet service",
      "Comfortable vehicles",
      "Punctual pickups",
      "24/7 availability"
    ]
  }
];

export default function Home() {
  const [_, navigate] = useLocation();
  const { t } = useLanguage();


  // ✅ Conversiobot Loader
  useEffect(() => {
    if (document.getElementById("bot-widget-script")) return;

    (window as any).botId = "hQvGCT";
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.id = "bot-widget-script";
    script.src = "https://app.conversiobot.com/lib/js/gadget.js";
    script.setAttribute("bid", "hQvGCT");
    document.head.appendChild(script);

    // After a short delay, reposition the bot bubble
    const repositionInterval = setInterval(() => {
      const botFrame = document.querySelector('iframe[src*="conversiobot"]') as HTMLElement;
      if (botFrame) {
        botFrame.style.position = "fixed";
        botFrame.style.bottom = "90px";
        botFrame.style.right = "20px";
        botFrame.style.zIndex = "999999";
        clearInterval(repositionInterval); // stop after positioning
      }
    }, 500);

    return () => {
      const existingScript = document.getElementById("bot-widget-script");
      if (existingScript) existingScript.remove();
      clearInterval(repositionInterval);
    };
  }, []);

  return (
    <div>
      <HeroSection />

      {/* Service Cards Section */}
      <section className="py-12 bg-neutral-50 -mt-8 relative z-30" style={{ backgroundImage: 'radial-gradient(#CBD5E1 1px, transparent 1px)', backgroundSize: '24px 24px' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Card 1 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              onClick={() => navigate("/destinations")}
              className="bg-white rounded-xl shadow-lg p-8 cursor-pointer hover:shadow-xl transition-all border-t-4 border-primary group"
            >
              <h3 className="text-xl font-bold mb-2 group-hover:text-primary transition-colors">Browse Safari Packages</h3>
              <p className="text-gray-600 mb-4">Ready-Made Itineraries</p>
              <div className="flex items-center text-primary font-medium">
                Explore <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </div>
            </motion.div>

            {/* Card 2 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              onClick={() => navigate("/booking")}
              className="bg-white rounded-xl shadow-lg p-8 cursor-pointer hover:shadow-xl transition-all border-t-4 border-primary group"
            >
              <h3 className="text-xl font-bold mb-2 group-hover:text-primary transition-colors">Custom Safari Planning</h3>
              <p className="text-gray-600 mb-4">Tell Us Your Budget and Travel Dates</p>
              <div className="flex items-center text-primary font-medium">
                Plan Now <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </div>
            </motion.div>

            {/* Card 3 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              onClick={() => navigate("/services/air-ticketing")}
              className="bg-white rounded-xl shadow-lg p-8 cursor-pointer hover:shadow-xl transition-all border-t-4 border-primary group"
            >
              <h3 className="text-xl font-bold mb-2 group-hover:text-primary transition-colors">Flights & Air Ticketing</h3>
              <p className="text-gray-600 mb-4">Book Your Travel</p>
              <div className="flex items-center text-primary font-medium">
                Book Flights <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <WhatsAppLeadCapture />

      <USPSection />

      <section id="popular-experiences" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4">Popular Safari Experiences</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Curated top-rated adventures for your perfect getaway.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {featuredItineraries.map((tour, index) => (
              <motion.div
                key={tour.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
              >
                <div className="relative h-64">
                  <OptimizedImage
                    src={tour.image}
                    alt={tour.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full">
                    <span className="text-sm font-medium text-gray-900">
                      {tour.duration}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {tour.title}
                  </h3>
                  <p className="text-gray-600 mb-4 line-clamp-2">
                    {tour.description}
                  </p>
                  <div className="space-y-4">
                    <div className="flex flex-wrap gap-2">
                      {tour.highlights.map((highlight, i) => (
                        <span
                          key={i}
                          className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm"
                        >
                          {highlight}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-semibold text-primary">
                        {tour.price}
                      </span>
                      <Button
                        onClick={() => navigate(tour.href)}
                        className="bg-primary hover:bg-primary/90"
                      >
                        {t('viewTour')}
                      </Button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      <ImageGrid />
      {/*<TourShowcase />*/}
      <section className="relative py-24 overflow-hidden bg-white">

        {/*<div
          className="absolute inset-0 bg-gradient-to-br from-indigo-50 to-purple-50"
          style={{
           backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='black' fill-opacity='0.08'/%3E%3C/svg%3E")`,
          }}
        />*/}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4">{t('services')}</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('servicesSubtitle')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
            {services.map((service, index) => (
              <ServiceCard
                key={service.title}
                {...service}
                delay={index * 0.2}
              />
            ))}
          </div>
        </div>
      </section>

      <ClientReviews />
      <Partners />
    </div>
  );
}